import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Chip } from '@mui/material';
import { getNoteFacets, NoteFacet } from '../../services/api';
import TabFilterBar from '../ui/TabFilterBar';

const TAG_DOT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];

/** Loads category/tag counts; refetches when refreshKey changes. */
export const useNoteFacets = (refreshKey?: number) => {
  const [categories, setCategories] = useState<NoteFacet[]>([]);
  const [tags, setTags] = useState<NoteFacet[]>([]);

  useEffect(() => {
    getNoteFacets()
      .then((data) => {
        setCategories(data.categories || []);
        setTags(data.tags || []);
      })
      .catch(() => {
        setCategories([]);
        setTags([]);
      });
  }, [refreshKey]);

  return { categories, tags };
};

type CategoryTabsProps = {
  categories: NoteFacet[];
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
  totalCount: number;
};

export const NotesCategoryTabs = ({ categories, selectedCategory, onSelectCategory, totalCount }: CategoryTabsProps) => (
  <TabFilterBar
    value={selectedCategory ?? '__all__'}
    onChange={(v) => onSelectCategory(v === '__all__' ? null : v)}
    options={[
      { value: '__all__', label: 'All', count: totalCount },
      ...categories.map((c) => ({ value: c.name, label: c.name, count: c.count })),
    ]}
    noMargin
  />
);

type Props = {
  tags: NoteFacet[];
  selectedCategory: string | null;
  selectedTag: string | null;
  onSelectCategory: (category: string | null) => void;
  onSelectTag: (tag: string | null) => void;
};

const NotesFilterPanel = ({ tags, selectedCategory, selectedTag, onSelectCategory, onSelectTag }: Props) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>Tags:</Typography>

        {tags.length === 0 && (
          <Typography variant="caption" color="text.secondary">Tag a note to see it here.</Typography>
        )}

        {tags.map((t, i) => {
          const active = selectedTag === t.name;
          const dotColor = TAG_DOT_COLORS[i % TAG_DOT_COLORS.length];
          return (
            <Chip
              key={t.name}
              onClick={() => onSelectTag(active ? null : t.name)}
              variant={active ? 'filled' : 'outlined'}
              color={active ? 'primary' : 'default'}
              size="small"
              icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, ml: '6px' }} />}
              label={`#${t.name} ${t.count}`}
            />
          );
        })}

        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 700 }}
          onClick={() => navigate('/notes/create')}
        >
          + Add Tag
        </Typography>

        {(selectedCategory || selectedTag) && (
          <Chip
            label="Clear filters"
            size="small"
            onClick={() => { onSelectCategory(null); onSelectTag(null); }}
          />
        )}

      </Stack>
    </Box>
  );
};

export default NotesFilterPanel;
